import type React from 'react'
import {
  Award, Calendar, FileText, Gift, GraduationCap, Handshake, Headphones, Heart, MessageSquare, Reply, Star, ThumbsUp, Trophy, Users
} from 'lucide-react'

/**
 * The level ladder, the same one the API uses (`src/utils/gamificationLevel.js`):
 * the minimum points of each level, 1 to 9. Names come from `ranking:levels.names.<n>`.
 */
export const LEVEL_MINIMUMS = [0, 100, 300, 600, 1000, 2000, 3500, 6000, 10000] as const
export const MAX_LEVEL = LEVEL_MINIMUMS.length

export const levelOf = (points: number) => {
  let level = 1
  LEVEL_MINIMUMS.forEach((min, i) => { if (points >= min) level = i + 1 })
  return level
}

/** Medal colors for positions 1 to 3: muted metal tones that sit beside any community color. */
export const MEDAL_COLORS: Record<1 | 2 | 3, { ring: string; soft: string; text: string }> = {
  1: { ring: '#d4a72c', soft: '#fbf3dc', text: '#8a6510' },
  2: { ring: '#a3acb9', soft: '#f1f3f6', text: '#4b5563' },
  3: { ring: '#c08457', soft: '#f8ece3', text: '#8a4b1f' }
}
export const medalFor = (rank: number) => (rank >= 1 && rank <= 3 ? MEDAL_COLORS[rank as 1 | 2 | 3] : null)

const ACTIONS: Record<string, { icon: React.ElementType; key: string }> = {
  CREATE_POST: { icon: MessageSquare, key: 'createPost' },
  LIKE_POST: { icon: Heart, key: 'likePost' },
  COMMENT_POST: { icon: MessageSquare, key: 'commentPost' },
  REPLY_COMMENT: { icon: Reply, key: 'replyComment' },
  PARTICIPATE_EVENT: { icon: Calendar, key: 'participateEvent' },
  CREATE_EVENT: { icon: Calendar, key: 'createEvent' },
  CREATE_COURSE: { icon: GraduationCap, key: 'createCourse' },
  ENROLL_COURSE: { icon: GraduationCap, key: 'enrollCourse' },
  COMPLETE_LESSON: { icon: GraduationCap, key: 'completeLesson' },
  RECEIVE_LIKE: { icon: ThumbsUp, key: 'receiveLike' },
  DAILY_LOGIN: { icon: Users, key: 'dailyLogin' },
  GIVE_RECOGNITION: { icon: Award, key: 'giveRecognition' },
  RECEIVE_RECOGNITION: { icon: Award, key: 'receiveRecognition' },
  CREATE_CONTENT: { icon: FileText, key: 'createContent' },
  NETWORKING_SESSION_HOSTED: { icon: Handshake, key: 'networkingSessionHosted' },
  NETWORKING_SESSION_ATTENDED: { icon: Handshake, key: 'networkingSessionAttended' },
  NETWORKING_REVIEW_GIVEN: { icon: Star, key: 'networkingReviewGiven' },
  NETWORKING_GROUP_SESSION_HOSTED: { icon: Handshake, key: 'networkingGroupSessionHosted' },
  /* F-178: an episode listened to the end, and a comment on one. */
  COMPLETE_EPISODE: { icon: Headphones, key: 'completeEpisode' },
  COMMENT_EPISODE: { icon: MessageSquare, key: 'commentEpisode' },
  /* F-159: someone joined through the person's referral link. */
  REFER_MEMBER: { icon: Gift, key: 'referMember' }
}

/** Icon and `ranking:actions.*` key of an action type; null for one the app has no words for (it is left out). */
export const actionInfo = (actionType: string) => {
  const found = ACTIONS[actionType]
  return found ? { icon: found.icon, labelKey: `ranking:actions.${found.key}` } : { icon: Trophy, labelKey: null }
}

/**
 * F-152: a level's name as the community named it. The API sends `levelName` (the default ones in the
 * reader's language); an older answer without it falls back to the default name of the number.
 */
export const levelNameOf = (t: (key: string, opts?: Record<string, unknown>) => string, level: number | null | undefined, name?: string | null): string =>
  name || t(`ranking:levels.names.${level ?? 1}`, { defaultValue: t('ranking:levels.short', { level: level ?? 1 }) })
